import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import type { AdvancePayeeRef } from "@/utils/cash-advance";
import { queryKeys } from "@/lib/query/keys";
import {
  liquidationReportsApi,
  type LiquidationReport,
  type CreateLiquidationReportData,
  type UpdateLiquidationReportData,
  type RecordCashReturnData,
} from "@/lib/api/liquidation-reports";

import { withQuerySignal } from "@/lib/api";
export type { LiquidationReport };

export function useLiquidationReports(params?: {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  isPettyCashReplenishment?: boolean;
  onlyUnlinked?: boolean;
  excludeDvId?: number;
}) {
  return useQuery({
    queryKey: queryKeys.liquidationReports.list(params),
    queryFn: ({ signal }) => withQuerySignal(signal, () => liquidationReportsApi.getAll(params)),
    placeholderData: keepPreviousData,
  });
}

/** What the report posted to the journal (a checked petty cash liquidation). */
export function useLiquidationReportJournal(id: number | null) {
  return useQuery({
    queryKey: queryKeys.liquidationReports.journal(id ?? 0),
    queryFn: () => liquidationReportsApi.journal(id!),
    enabled: !!id,
  });
}

export function useLiquidationReport(id: number, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.liquidationReports.detail(id),
    queryFn: () => liquidationReportsApi.getById(id),
    enabled: options?.enabled ?? id > 0,
  });
}

/**
 * Resolve a liquidation report by NUMBER, for `?view=<number>` deep links.
 *
 * Two requests, because there is no by-number endpoint the way there is for
 * vouchers and purchase orders: search the list, then load the exact match in
 * full. Adding a server route would match the other forms better, but this needs
 * no API surface and the deep link is the same either way.
 *
 * The EXACT comparison matters. `search` is a contains-match, so "LR-2026-001"
 * also returns "LR-2026-0012" — taking the first result would open the wrong
 * report, which is worse than not opening one at all.
 */
export function useLiquidationReportByNumber(number: string | null) {
  const list = useLiquidationReports(number ? { search: number, limit: 20 } : undefined);
  const match = number ? list.data?.reports.find((r) => r.number === number) : undefined;
  const detail = useLiquidationReport(match?.id ?? 0, { enabled: !!match });

  return {
    data: detail.data,
    // Still resolving while either leg is in flight, so the caller does not
    // flash "not found" between the search landing and the detail arriving.
    isLoading: (!!number && list.isLoading) || (!!match && detail.isLoading),
    /** The number resolved to nothing — a dead link, not a slow one. */
    notFound: !!number && !list.isLoading && !match,
  };
}

export function useCreateLiquidationReport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateLiquidationReportData) => liquidationReportsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.liquidationReports.lists() });
    },
  });
}

export function useUpdateLiquidationReport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateLiquidationReportData }) =>
      liquidationReportsApi.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.liquidationReports.detail(id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.liquidationReports.lists() });
    },
  });
}

export function useSubmitLiquidationReport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => liquidationReportsApi.submit(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.liquidationReports.detail(id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.liquidationReports.lists() });
    },
  });
}

export function useCheckLiquidationReport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, comment }: { id: number; comment?: string }) =>
      liquidationReportsApi.check(id, comment),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.liquidationReports.detail(id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.liquidationReports.history(id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.liquidationReports.lists() });
    },
  });
}

export function useReturnToDraftLiquidationReport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, comment }: { id: number; comment: string }) =>
      liquidationReportsApi.returnToDraft(id, comment),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.liquidationReports.detail(id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.liquidationReports.history(id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.liquidationReports.lists() });
    },
  });
}

export function useRejectLiquidationReport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, comment }: { id: number; comment: string }) =>
      liquidationReportsApi.reject(id, comment),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.liquidationReports.detail(id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.liquidationReports.history(id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.liquidationReports.lists() });
    },
  });
}

export function useCancelLiquidationReport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, comment }: { id: number; comment: string }) =>
      liquidationReportsApi.cancel(id, comment),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.liquidationReports.detail(id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.liquidationReports.history(id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.liquidationReports.lists() });
    },
  });
}

export function useLiquidationReportHistory(id: number | null) {
  return useQuery({
    queryKey: queryKeys.liquidationReports.history(id || 0),
    queryFn: () => liquidationReportsApi.getHistory(id!),
    enabled: !!id,
  });
}

export function useDeleteLiquidationReport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => liquidationReportsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.liquidationReports.lists() });
    },
  });
}

export function useGenerateLiquidationReportPdf() {
  return useMutation({
    mutationFn: (id: number) => liquidationReportsApi.generatePdf(id),
  });
}

// Presigned URL to the stored S3 PDF — fast preview (no Puppeteer re-render).
// Returns { url: null } for reports with no stored document yet (e.g. drafts).
export function useLiquidationReportDocument(id: number | null) {
  return useQuery({
    queryKey: queryKeys.liquidationReports.document(id || 0),
    queryFn: () => liquidationReportsApi.getDocument(id!),
    enabled: !!id,
  });
}

// Per-item receipts (image / PDF), each with an inline presigned URL.
export function useLiquidationReportReceipts(id: number | null) {
  return useQuery({
    queryKey: queryKeys.liquidationReports.receipts(id || 0),
    queryFn: () => liquidationReportsApi.getReceipts(id!),
    enabled: !!id,
  });
}

export function useUploadLiquidationReceipt() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ reportId, sortOrder, file }: { reportId: number; sortOrder: number; file: File }) =>
      liquidationReportsApi.uploadReceipt(reportId, sortOrder, file),
    onSuccess: (_, { reportId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.liquidationReports.receipts(reportId) });
    },
  });
}

export function useDeleteLiquidationReceipt() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ reportId, receiptId }: { reportId: number; receiptId: number }) =>
      liquidationReportsApi.deleteReceipt(reportId, receiptId),
    onSuccess: (_, { reportId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.liquidationReports.receipts(reportId) });
    },
  });
}

// ---- Cash advance liquidations ----

/** The employee's cash advances a report may still take in (pass the report when editing). */
export function useOpenCashAdvances(payee: AdvancePayeeRef | null, exceptReportId?: number | null) {
  const key = !payee ? "none" : "vendorId" in payee ? `v:${payee.vendorId}` : `e:${payee.employeeStaffId}`;
  return useQuery({
    queryKey: queryKeys.liquidationReports.openAdvances(key, exceptReportId),
    queryFn: ({ signal }) => withQuerySignal(signal, () => liquidationReportsApi.getOpenAdvances(payee!, exceptReportId)),
    enabled: !!payee,
  });
}

/** Records unspent cash handed back on a checked cash advance liquidation. */
export function useRecordCashReturn() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ reportId, data }: { reportId: number; data: RecordCashReturnData }) =>
      liquidationReportsApi.recordCashReturn(reportId, data),
    onSuccess: (_, { reportId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.liquidationReports.detail(reportId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.liquidationReports.history(reportId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.liquidationReports.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.liquidationReports.journal(reportId) });
      queryClient.invalidateQueries({ queryKey: [...queryKeys.liquidationReports.all, "openAdvances"] });
      queryClient.invalidateQueries({ queryKey: ["disbursementVouchers"] });
    },
  });
}

/** Removes a cash return recorded by mistake (its entry is reversed). */
export function useDeleteCashReturn() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ reportId, returnId }: { reportId: number; returnId: number }) =>
      liquidationReportsApi.deleteCashReturn(reportId, returnId),
    onSuccess: (_, { reportId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.liquidationReports.detail(reportId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.liquidationReports.history(reportId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.liquidationReports.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.liquidationReports.journal(reportId) });
      queryClient.invalidateQueries({ queryKey: [...queryKeys.liquidationReports.all, "openAdvances"] });
      queryClient.invalidateQueries({ queryKey: ["disbursementVouchers"] });
    },
  });
}
